import {
  Children,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { cn } from 'components/UI/cn'
import { useForm, useController } from 'react-hook-form'
import useApiService from 'services/api'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import to from 'await-to-js'
import { Plus, X } from 'lucide-react'
import { handleFormError } from 'utils/handleFormError'
import { CompanyPageAccordion } from './CompanyPageAccordion'
import { AttributePickerModal, PickerOption } from './AttributePickerModal'
import { CreateCampaignModalV2 } from 'features/campaigns/CreateCampaignModalV2'
import FbButton from 'components/FbUI/FbButton'
import { Tag } from 'models/tags'
import {
  CompanyDistributor,
  CompanyGPO,
  CompanyTypeSlug,
} from 'models/companies'
import { useCompanyOrDoorData } from 'hooks/useCompanyOrDoorData'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'

const attributesSchema = z.object({
  taglist: z.any().optional().nullable(),
  distributors: z.any().optional().nullable(),
  gpos: z.any().optional().nullable(),
  campaigns: z.any().optional().nullable(),
  previous_taglist: z.any().optional().nullable(),
  previous_distributors: z.any().optional().nullable(),
  previous_gpos: z.any().optional().nullable(),
  previous_campaigns: z.any().optional().nullable(),
})

export type CompanyAttributesInput = z.infer<typeof attributesSchema>

interface CompanyAttributesAccordionProps {
  chainProxyId: number
  doorProxyId?: number
  companyType?: CompanyTypeSlug
}

export function CompanyAttributesAccordion({
  chainProxyId,
  doorProxyId,
  companyType = 'restaurants-bars',
}: CompanyAttributesAccordionProps) {
  const queryClient = useQueryClient()
  const api = useApiService()

  // Fetch options for selectors
  const { data: distributorsOptions } = api.useGetCompanyDistributorsOptions()
  const { data: gposOptions } = api.useGetCompanyGPOsOptions()
  const { data: tagsOptions } = api.useGetTagsOptions()
  const { data: campaignsOptions } = api.useGetCampaignsOptions({
    typeOrTypeId: companyType,
    requireFilter: true, // Prevent selecting campaigns when company type is unknown
  })

  // Fetch company or door proxy data based on context
  const { data: effectiveData, refetch: refetchData } = useCompanyOrDoorData(
    chainProxyId,
    doorProxyId
  )

  const [previousDistributors, setPreviousDistributors] = useState<number[]>([])
  const [previousGPOs, setPreviousGPOs] = useState<number[]>([])
  const [previousTaglist, setPreviousTaglist] = useState<number[]>([])
  const [previousCampaigns, setPreviousCampaigns] = useState<number[]>([])

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    formState,
  } = useForm<CompanyAttributesInput>({
    defaultValues: {
      taglist: undefined,
      distributors: undefined,
      gpos: undefined,
      campaigns: undefined,
    },
    resolver: zodResolver(attributesSchema),
  })

  // Subscribe to each field via useController. We previously used bare
  // register() + useWatch, but on some pages useWatch returned undefined
  // even after reset() populated the values — useController is the
  // canonical way to bind RHF state to non-input UI and gives us a
  // reliable subscription.
  const {
    field: { value: taglistValue },
  } = useController({
    name: 'taglist',
    control,
  })
  const {
    field: { value: campaignsValue },
  } = useController({
    name: 'campaigns',
    control,
  })
  const {
    field: { value: distributorsValue },
  } = useController({
    name: 'distributors',
    control,
  })
  const {
    field: { value: gposValue },
  } = useController({
    name: 'gpos',
    control,
  })

  // Initialize form with company or door proxy data
  useEffect(() => {
    if (effectiveData) {
      const taglist: Tag[] = effectiveData.taglist || []
      const distributors: CompanyDistributor[] =
        effectiveData.distributors || []
      const gpos: CompanyGPO[] = effectiveData.gpos || []
      const campaigns: any[] = (effectiveData as any).campaigns || []

      const mappedTaglist = taglist.map((t) => t.id) || []
      const mappedDistributors =
        distributors.map((distributor) => distributor.id) || []
      const mappedGPOs = gpos.map((gpo) => gpo.id) || []
      const mappedCampaigns = campaigns.map((campaign) => campaign.id) || []

      reset({
        taglist: mappedTaglist,
        distributors: mappedDistributors,
        gpos: mappedGPOs,
        campaigns: mappedCampaigns,
      })

      setPreviousDistributors(mappedDistributors)
      setPreviousGPOs(mappedGPOs)
      setPreviousTaglist(mappedTaglist)
      setPreviousCampaigns(mappedCampaigns)
    }
  }, [effectiveData, reset])

  const mutation = useMutation({
    mutationFn: async (values: CompanyAttributesInput) => {
      if (doorProxyId) {
        await api.bulkEditDoorAttrs({
          door_proxy_ids: [doorProxyId],
          taglist:
            Array.isArray(values.taglist) && values.taglist.length > 0
              ? values.taglist
              : 'REMOVE',
          distributors:
            Array.isArray(values.distributors) && values.distributors.length > 0
              ? values.distributors
              : 'REMOVE',
          gpos:
            Array.isArray(values.gpos) && values.gpos.length > 0
              ? values.gpos
              : 'REMOVE',
          previous_distributors: previousDistributors,
          previous_gpos: previousGPOs,
          previous_taglist: previousTaglist,
        })
      } else {
        await api.bulkEditCompanyAttrs({
          contact_company_ids: [chainProxyId],
          taglist:
            Array.isArray(values.taglist) && values.taglist.length > 0
              ? values.taglist
              : 'REMOVE',
          distributors:
            Array.isArray(values.distributors) && values.distributors.length > 0
              ? values.distributors
              : 'REMOVE',
          gpos:
            Array.isArray(values.gpos) && values.gpos.length > 0
              ? values.gpos
              : 'REMOVE',
          campaigns:
            Array.isArray(values.campaigns) && values.campaigns.length > 0
              ? values.campaigns
              : 'REMOVE',
          previous_distributors: previousDistributors,
          previous_gpos: previousGPOs,
          previous_taglist: previousTaglist,
          previous_campaigns: previousCampaigns,
        })
      }
    },
    onSettled: async (data, err) => {
      refetchData()
      queryClient.invalidateQueries({ queryKey: ['notes-table'] })
      queryClient.invalidateQueries({
        queryKey: ['companyActivity', chainProxyId, doorProxyId],
      })
      if (err) {
        toast.error(
          `Error updating ${doorProxyId ? 'door' : 'company'} attributes.`
        )
      } else {
        toast.success(
          `${doorProxyId ? 'Door' : 'Company'} attributes updated successfully.`
        )
        reset(formState.defaultValues)
      }
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    mutation.mutate(values)
  })

  const tagsList = Array.isArray(taglistValue) ? (taglistValue as number[]) : []
  const campaignsList = Array.isArray(campaignsValue)
    ? (campaignsValue as number[])
    : []
  const distributorsList = Array.isArray(distributorsValue)
    ? (distributorsValue as number[])
    : []
  const gposList = Array.isArray(gposValue) ? (gposValue as number[]) : []

  // Per-type lookup of {label, color?} keyed by id. Sources from BOTH
  // the saved company data (preferred — has the latest server-side
  // values) AND the full options endpoint (fallback — covers picker
  // additions that haven't been saved yet). Without the fallback,
  // newly-picked ids wouldn't resolve and the pills would silently
  // skip rendering until after save + refetch.
  const tagsById = useMemo(() => {
    const m = new Map<number, { label: string; color?: string }>()
    for (const o of (tagsOptions as any[]) ?? []) {
      m.set(o.value, { label: o.label, color: o.original?.color })
    }
    for (const t of (effectiveData?.taglist ?? []) as Tag[]) {
      m.set(t.id, { label: t.tag, color: t.color })
    }
    return m
  }, [tagsOptions, effectiveData?.taglist])

  const campaignsById = useMemo(() => {
    const m = new Map<number, { label: string; color?: string }>()
    for (const o of (campaignsOptions as any[]) ?? []) {
      m.set(o.value, { label: o.label, color: o.color })
    }
    for (const c of ((effectiveData as any)?.campaigns ?? []) as any[]) {
      m.set(c.id, { label: c.name, color: c.color })
    }
    return m
  }, [campaignsOptions, effectiveData])

  const distributorsById = useMemo(() => {
    const m = new Map<number, { label: string }>()
    for (const o of (distributorsOptions as any[]) ?? []) {
      m.set(o.value, { label: o.label })
    }
    for (const d of (effectiveData?.distributors ??
      []) as CompanyDistributor[]) {
      m.set(d.id, { label: d.name })
    }
    return m
  }, [distributorsOptions, effectiveData?.distributors])

  const gposById = useMemo(() => {
    const m = new Map<number, { label: string }>()
    for (const o of (gposOptions as any[]) ?? []) {
      m.set(o.value, { label: o.label })
    }
    for (const g of (effectiveData?.gpos ?? []) as CompanyGPO[]) {
      m.set(g.id, { label: g.name })
    }
    return m
  }, [gposOptions, effectiveData?.gpos])

  const paletteFor = (color?: string) =>
    MULTI_VALUE_SELECT_COLORS.find((c) => c.background === color) ??
    MULTI_VALUE_SELECT_COLORS[0]

  // Pill X removes are autosaved — the Save button never appears for
  // these. setValue uses shouldDirty:false so the optimistic form
  // update doesn't surface the Save button; the mutation's refetch +
  // the effectiveData useEffect re-sync the form afterwards.
  const handleRemove = (
    name: 'taglist' | 'campaigns' | 'distributors' | 'gpos',
    id: number
  ) => {
    const current = (getValues(name) as number[] | undefined) ?? []
    const newList = current.filter((v) => v !== id)
    setValue(name, newList, { shouldDirty: false })
    mutation.mutate({ ...getValues(), [name]: newList })
  }

  const removeFromList =
    (name: 'taglist' | 'campaigns' | 'distributors' | 'gpos') =>
    (id: number) => {
      handleRemove(name, id)
    }

  // Re-reads the latest form value each call so the modal can commit
  // multiple staged ids in sequence without earlier writes being
  // clobbered by a stale closure-captured array.
  const addToList =
    (name: 'taglist' | 'campaigns' | 'distributors' | 'gpos') =>
    (id: number) => {
      const current = (getValues(name) as number[] | undefined) ?? []
      if (current.includes(id)) return
      setValue(name, [...current, id], { shouldDirty: true })
    }

  // Picker options (from the full options endpoints).
  const tagPickerOptions: PickerOption[] = ((tagsOptions as any[]) ?? []).map(
    (o) => ({
      label: o.label,
      value: o.value,
      color: o.original?.color,
    })
  )
  const campaignPickerOptions: PickerOption[] = (
    (campaignsOptions as any[]) ?? []
  ).map((o) => ({ label: o.label, value: o.value, color: o.color }))
  const distributorPickerOptions: PickerOption[] = (
    (distributorsOptions as any[]) ?? []
  ).map((o) => ({ label: o.label, value: o.value }))
  const gpoPickerOptions: PickerOption[] = ((gposOptions as any[]) ?? []).map(
    (o) => ({ label: o.label, value: o.value })
  )

  const createTag = async (value: string): Promise<number | null> => {
    if (value.length > 50) {
      toast.error('Tag length limited to 50 characters.')
      return null
    }
    const [err, res] = await to(api.postTag({ tag: value }, 'products'))
    if (err) {
      handleFormError(err, setError)
      return null
    }
    queryClient.invalidateQueries({ queryKey: ['tags', 'products'] })
    return (res as any)?.id ?? null
  }

  // Campaign creation goes through the full CreateCampaignModalV2 (not
  // a bare-name POST) so users fill in product/owner/launch date/etc.
  const [campaignCreateName, setCampaignCreateName] = useState<string | null>(
    null
  )
  const launchCreateCampaign = (value: string) => {
    setCampaignCreateName(value)
    setAdding(null)
  }

  const createDistributor = async (value: string): Promise<number | null> => {
    const [err, res] = await to(api.postDistributor({ name: value }))
    if (err) {
      handleFormError(err, setError)
      return null
    }
    queryClient.invalidateQueries({ queryKey: ['distributors'] })
    return (res as any)?.id ?? null
  }

  const createGPO = async (value: string): Promise<number | null> => {
    const [err, res] = await to(api.postGPO({ name: value }))
    if (err) {
      handleFormError(err, setError)
      return null
    }
    queryClient.invalidateQueries({ queryKey: ['gpos'] })
    return (res as any)?.id ?? null
  }

  // Single adding-type tracker — only one picker modal open at a time.
  type AddingType = 'tag' | 'campaign' | 'distributor' | 'gpo'
  const [adding, setAdding] = useState<AddingType | null>(null)

  // Source counts from the form state so the summary can't disagree
  // with what's visible.
  const tagsCount = tagsList.length
  const campaignsCount = campaignsList.length
  const distributorsCount = distributorsList.length
  const gposCount = gposList.length

  // Inline summary string shown in the accordion's title row, dot-separated.
  const summaryParts = [
    tagsCount > 0 && `${tagsCount} ${tagsCount === 1 ? 'Tag' : 'Tags'}`,
    !doorProxyId &&
      campaignsCount > 0 &&
      `${campaignsCount} ${campaignsCount === 1 ? 'Campaign' : 'Campaigns'}`,
    distributorsCount > 0 &&
      `${distributorsCount} ${distributorsCount === 1 ? 'Distributor' : 'Distributors'}`,
    gposCount > 0 && `${gposCount} ${gposCount === 1 ? 'GPO' : 'GPOs'}`,
  ].filter(Boolean) as string[]

  // Accordion auto-open decision is based on the *saved* data (not
  // form state) so we only remount once — straight from 'loading' to
  // 'has-data' or 'empty' — instead of bouncing through an extra
  // 'empty' state while the form is being reset from effectiveData.
  const savedHasAttributes =
    (effectiveData?.taglist?.length ?? 0) > 0 ||
    (!doorProxyId && ((effectiveData as any)?.campaigns?.length ?? 0) > 0) ||
    (effectiveData?.distributors?.length ?? 0) > 0 ||
    (effectiveData?.gpos?.length ?? 0) > 0
  const accordionKey = effectiveData
    ? savedHasAttributes
      ? 'has-data'
      : 'empty'
    : 'loading'

  return (
    <CompanyPageAccordion
      key={accordionKey}
      title="Account Attributes"
      defaultOpen={savedHasAttributes}
      headerRight={
        summaryParts.length > 0 ? (
          <span className="text-sm text-gray-400 tabular-nums">
            {summaryParts.join(' · ')}
          </span>
        ) : undefined
      }
      bgClass="bg-gray-50"
    >
      <div className="space-y-3">
        {/* Tags + Campaigns (Campaigns aren't supported on door pages, so
            on those Tags goes full-width). */}
        {doorProxyId ? (
          <PillRowSection
            label="Tags"
            addBadge={<AddBadge onClick={() => setAdding('tag')} />}
          >
            {tagsList.map((id) => {
              const t = tagsById.get(id)
              if (!t) return null
              const palette = paletteFor(t.color)
              return (
                <AccountPill
                  key={id}
                  label={t.label}
                  background={palette.background}
                  textColor={palette.text}
                  onRemove={() => removeFromList('taglist')(id)}
                />
              )
            })}
          </PillRowSection>
        ) : (
          <div className="flex flex-col gap-3 xl:grid xl:grid-cols-2 xl:gap-3 xl:items-start">
            <PillRowSection
              label="Tags"
              addBadge={<AddBadge onClick={() => setAdding('tag')} />}
            >
              {tagsList.map((id) => {
                const t = tagsById.get(id)
                if (!t) return null
                const palette = paletteFor(t.color)
                return (
                  <AccountPill
                    key={id}
                    label={t.label}
                    background={palette.background}
                    textColor={palette.text}
                    onRemove={() => removeFromList('taglist')(id)}
                  />
                )
              })}
            </PillRowSection>

            <PillRowSection
              label="Campaigns"
              addBadge={<AddBadge onClick={() => setAdding('campaign')} />}
            >
              {campaignsList.map((id) => {
                const c = campaignsById.get(id)
                if (!c) return null
                const palette = paletteFor(c.color)
                return (
                  <AccountPill
                    key={id}
                    label={c.label}
                    background={palette.background}
                    textColor={palette.text}
                    href={`/campaigns/${id}`}
                    onRemove={() => removeFromList('campaigns')(id)}
                  />
                )
              })}
            </PillRowSection>
          </div>
        )}

        {/* Distributors + GPOs: same stacking behavior as Tags + Campaigns. */}
        <div className="flex flex-col gap-3 xl:grid xl:grid-cols-2 xl:gap-3 xl:items-start">
          <PillRowSection
            label="Distributors"
            addBadge={<AddBadge onClick={() => setAdding('distributor')} />}
          >
            {distributorsList.map((id) => {
              const d = distributorsById.get(id)
              if (!d) return null
              return (
                <AccountPill
                  key={id}
                  label={d.label}
                  onRemove={() => removeFromList('distributors')(id)}
                />
              )
            })}
          </PillRowSection>

          <PillRowSection
            label="Group Purchasing Organization (GPO)"
            addBadge={<AddBadge onClick={() => setAdding('gpo')} />}
          >
            {gposList.map((id) => {
              const g = gposById.get(id)
              if (!g) return null
              return (
                <AccountPill
                  key={id}
                  label={g.label}
                  onRemove={() => removeFromList('gpos')(id)}
                />
              )
            })}
          </PillRowSection>
        </div>

        <AttributePickerModal
          open={adding === 'tag'}
          onOpenChange={(o) => setAdding(o ? 'tag' : null)}
          title="Add tag"
          itemNoun="tag"
          options={tagPickerOptions}
          selectedIds={tagsList}
          onSelect={addToList('taglist')}
          onCreate={createTag}
        />
        {!doorProxyId && (
          <AttributePickerModal
            open={adding === 'campaign'}
            onOpenChange={(o) => setAdding(o ? 'campaign' : null)}
            title="Add to Campaign"
            itemNoun="campaign"
            options={campaignPickerOptions}
            selectedIds={campaignsList}
            onSelect={addToList('campaigns')}
            onCreateExternal={launchCreateCampaign}
          />
        )}
        <AttributePickerModal
          open={adding === 'distributor'}
          onOpenChange={(o) => setAdding(o ? 'distributor' : null)}
          title="Add distributor"
          itemNoun="distributor"
          options={distributorPickerOptions}
          selectedIds={distributorsList}
          onSelect={addToList('distributors')}
          onCreate={createDistributor}
        />
        <AttributePickerModal
          open={adding === 'gpo'}
          onOpenChange={(o) => setAdding(o ? 'gpo' : null)}
          title="Add GPO"
          itemNoun="GPO"
          options={gpoPickerOptions}
          selectedIds={gposList}
          onSelect={addToList('gpos')}
          onCreate={createGPO}
        />

        {!doorProxyId && (
          <CreateCampaignModalV2
            show={campaignCreateName !== null}
            onHide={() => setCampaignCreateName(null)}
            defaultName={campaignCreateName ?? ''}
            defaultTypeSlug={companyType}
            onCreated={(campaign) => {
              addToList('campaigns')(campaign.id)
              setCampaignCreateName(null)
              queryClient.invalidateQueries({ queryKey: ['campaigns'] })
            }}
          />
        )}

        {/* Save Button - Only show when form is dirty */}
        {formState.isDirty && (
          <div className="flex justify-end pt-4">
            <FbButton
              loading={mutation.isPending}
              onClick={onSubmit}
              className="bg-brand-hover hover:bg-brand-dark text-white"
            >
              Save
            </FbButton>
          </div>
        )}
      </div>
    </CompanyPageAccordion>
  )
}

function PillRowSection({
  label,
  children,
  addBadge,
}: {
  label: string
  children: React.ReactNode
  /** Rendered to the right of the clipped pill row, always visible. */
  addBadge: React.ReactNode
}) {
  // Drops nulls so the row is always a flat array of real elements.
  const items = Children.toArray(children).filter(Boolean)
  const pillsRef = useRef<HTMLDivElement>(null)
  const [hiddenCount, setHiddenCount] = useState(0)
  const [expanded, setExpanded] = useState(false)

  // Measure how many pills wrap past row 1. Re-runs when the pill count
  // changes and on container resize via ResizeObserver.
  useLayoutEffect(() => {
    const container = pillsRef.current
    if (!container) return

    const measure = () => {
      const childEls = Array.from(container.children) as HTMLElement[]
      if (childEls.length === 0) {
        setHiddenCount(0)
        return
      }
      const baseTop = childEls[0].getBoundingClientRect().top
      let hidden = 0
      for (const el of childEls) {
        if (el.getBoundingClientRect().top - baseTop > 2) hidden++
      }
      setHiddenCount(hidden)
    }

    measure()
    const ro = new ResizeObserver(() => measure())
    ro.observe(container)
    return () => ro.disconnect()
  }, [items.length])

  const isEmpty = items.length === 0

  return (
    <div className="relative bg-white rounded-lg p-3 shadow-sm">
      {/* Label row: section title on the left, Add button absolutely
          positioned at the top-right with a tight 8px inset (while
          the rest of the card uses p-3 = 12px). When there are no
          pills, the row below is omitted entirely. */}
      <div
        className={cn(
          'text-base font-normal leading-none text-gray-500 pr-12',
          !isEmpty && 'mb-3'
        )}
      >
        {label}
      </div>
      <div className="absolute top-2 right-2">{addBadge}</div>
      {!isEmpty && (
        <div className="flex items-start gap-1.5">
          <div
            ref={pillsRef}
            className={cn(
              'flex flex-wrap gap-1.5 flex-1 min-w-0',
              !expanded && 'max-h-6 overflow-hidden'
            )}
          >
            {items}
            {/* When expanded, "Show less" flows inline as the final
                item in the pill row so it appears at the end of the
                last row. ml-auto pushes it right; -mr-3 cancels the
                card's p-3 so its trailing edge aligns with the
                absolutely-positioned Add button at top-2 right-2. */}
            {expanded && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="ml-auto self-end text-sm font-medium text-brand-bright hover:text-brand-active transition-colors bg-transparent border-none p-0 cursor-pointer flex-shrink-0 tabular-nums"
              >
                Show less
              </button>
            )}
          </div>
          {!expanded && hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="self-end text-sm font-medium text-brand-bright hover:text-brand-active transition-colors bg-transparent border-none p-0 cursor-pointer flex-shrink-0 tabular-nums"
            >
              +{hiddenCount} more
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** Small outline icon-only button — Figma design system "Size=sm,
 *  Type=Outline, Content=Only Icon". 32×32 white square with a gray
 *  border. States from Figma:
 *   - Default: white bg, gray-300 border, gray-700 icon
 *   - Hover:   gray-100 bg
 *   - Focus:   4px gray halo shadow
 *   - Disabled: gray-100 border, faded gray-300 icon
 *  Reused for Edit on deal cards so attribute Add + deal Edit feel
 *  like the same affordance. */
function AddBadge({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Add"
      className="inline-flex size-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 transition-colors hover:!bg-gray-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gray-300/30 disabled:!bg-white disabled:!border-gray-100 disabled:!text-gray-300 disabled:cursor-not-allowed"
    >
      <Plus className="size-4" />
    </button>
  )
}

/** Renders the pill label with CSS truncation. Only sets the native
 *  `title` attribute when the label is actually overflowing — that
 *  way short pills don't show a redundant browser tooltip on hover. */
function TruncatedPillLabel({ label }: { label: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [label])

  return (
    <span
      ref={ref}
      className="truncate"
      title={isTruncated ? label : undefined}
    >
      {label}
    </span>
  )
}

function AccountPill({
  label,
  background,
  textColor,
  onRemove,
  href,
}: {
  label: string
  background?: string
  textColor?: string
  onRemove: () => void
  /** When provided, the label becomes a link that opens in a new
   *  tab. The X stays as a separate button. */
  href?: string
}) {
  const bg = background ?? '#E3E1DA'
  const text = textColor ?? '#464646'
  return (
    <span
      className="group inline-flex items-center gap-1 pl-2.5 pr-1 h-6 max-w-[200px] rounded-full text-sm font-medium"
      style={{ backgroundColor: bg, color: text }}
    >
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-w-0 hover:underline"
          style={{ color: text }}
        >
          <TruncatedPillLabel label={label} />
        </a>
      ) : (
        <TruncatedPillLabel label={label} />
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="w-4 h-4 flex-shrink-0 inline-flex items-center justify-center rounded-full opacity-50 group-hover:opacity-100 hover:!opacity-100 hover:bg-black/10 transition-opacity"
        style={{ color: text }}
      >
        <X className="w-3 h-3" strokeWidth={2.5} stroke={text} />
      </button>
    </span>
  )
}
